import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  loadMultiSettings,
  multiActionsTabsQuery,
  multiCheckAlive,
  multiNotifyWhenAlive,
  multiUninstall,
  multiWakeUp,
  saveMultiSettings,
  sendMultiMessage,
  type MultiSaved,
  type MultiTabKey,
} from '../api/overviewMultiActions'
import type { Named } from '../api/shell'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { MultiModulesTab } from './MultiModulesTab'

/**
 * Zona care se deschide in dreapta cand se bifeaza randuri in lista Managed.
 *
 * In portal e `MultiActionsSidebar` din `public/js/general.js`, deschisa de
 * `MultipleRowsSelect` prin evenimentele `multiRowsAllChecked` /
 * `multiRowsAllUnchecked`, cu continutul incarcat din
 * `overview/devicesMultiActions` si cu fiecare fila alt cap de controller.
 * Aici e acelasi markup (`.multiactions-sidebar`, `.page-quick-sidebar-*`,
 * `.multiple-actions-nav`, `#multiple-actions-content`), deci si aceleasi
 * reguli din `public/css/app.css`, dar filele cer DATE si isi deseneaza
 * singure formularul.
 *
 * Regula de aratat/ascuns e cea din portal, litera cu litera: panoul apare cand
 * e bifat cel putin un rand si dispare cand se scoate ultima bifa. Butonul de
 * inchis il inchide fara sa scoata bifele - urmatoarea schimbare de bifa il
 * deschide la loc, exact ca `MultiActionsSidebar.closeSidebar()`.
 */

function apiToast(err: unknown) {
  const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Something went wrong'
  toast(message, 'Error', 'error')
}

/** `Are you sure?` din ajaxForm-urile filelor. */
function confirmed(question = 'Are you sure?'): boolean {
  return window.confirm(question)
}

/**
 * Casuta desenata, ca la iCheck.
 *
 * Portalul nu arata niciodata casuta browserului: `DeviceMultiSettings` si
 * `DeviceMultiModuleSettings` trec fiecare input prin iCheck cu skinul
 * `icheckbox_flat-red` / `iradio_flat-red`, adica un patrat dintr-un sprite
 * (`public/css/app.css`). Fara invelisul ala, casuta browserului cade PESTE
 * eticheta - "Auto update" se citea "ito update". Acelasi invelis pe care il
 * pune `dressIcheck` in DataTable: inputul adevarat ramane tinta clicului,
 * intins peste patrat si transparent.
 */
function ICheck({
  type = 'checkbox',
  className,
  checked,
  disabled,
  onChange,
  ...rest
}: {
  type?: 'checkbox' | 'radio'
  className?: string
  checked: boolean
  disabled?: boolean
  onChange(on: boolean): void
  name?: string
  value?: string | number
  id?: string
  'data-module'?: number
}) {
  const skin = type === 'radio' ? 'iradio_flat-red' : 'icheckbox_flat-red'
  return (
    <div
      className={`${skin}${checked && !disabled ? ' checked' : ''}${disabled ? ' disabled' : ''}`}
      style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <input
        {...rest}
        type={type}
        className={className}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
          border: 0,
          opacity: 0,
          cursor: disabled ? 'default' : 'pointer',
        }}
      />
    </div>
  )
}

function Collapse({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="widget-collapse dark">
      <header>
        <a>
          <i className="collapse-caret fa fa-angle-up"></i> {title}
        </a>
      </header>
      <section className="collapse in" style={{ height: 'auto' }}>
        <div className="collapse-body" style={{ padding: 40 }}>
          {children}
        </div>
      </section>
    </div>
  )
}

/**
 * `overview/multiple_settings`.
 *
 * Casuta "User" e cea din macheta veche (`device_owner`): in portal lista ei
 * venea goala prin cod (`$default['owners'] = []`, interogarea comentata), dar
 * scrierea - proprietarul in CMDB, `Devices::updateMultipleDevices` - a fost
 * intotdeauna a ecranului. Aici lista vine de la `MultiActions::settingsForm`.
 */
function SettingsTab({ ids }: { ids: number[] }) {
  const [offices, setOffices] = useState<Named[]>([])
  const [owners, setOwners] = useState<Named[]>([])
  const [error, setError] = useState<string | null>(null)
  const [owner, setOwner] = useState('')
  const [office, setOffice] = useState('')
  const [classified, setClassified] = useState('')
  const [autoUpdate, setAutoUpdate] = useState(true)
  const [beta, setBeta] = useState(false)

  useEffect(() => {
    let gone = false
    setError(null)
    void loadMultiSettings(ids)
      .then((page) => {
        if (!gone) {
          setOffices(page.offices)
          setOwners(page.owners ?? [])
        }
      })
      .catch((err: unknown) => {
        if (!gone) setError(err instanceof Error ? err.message : 'Error')
      })
    return () => {
      gone = true
    }
  }, [ids.join(',')])

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (!confirmed()) return
    void saveMultiSettings(ids, {
      device_owner: owner,
      device_office: office,
      device_classified_info: classified,
      agent_auto_update: autoUpdate ? '1' : '',
      agent_beta_version: beta ? '1' : '',
    })
      .then(() => toast('Settings saved!', 'Success', 'success'))
      .catch(apiToast)
  }

  if (error) return <div className="alert alert-danger">{error}</div>

  return (
    <Collapse title="Device Settings">
      <div className="alert alert-info">
        <i className="fas fa-info-circle"></i> If nothing is selected the settings remains the same ( applies to{' '}
        <strong>User, Office, Classified info</strong>).
      </div>
      <form className="form-horizontal updateDeviceSettings" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>User</span>
          </label>
          <div className="col-sm-9">
            <SelectPicker
              id="multiSettingsOwner"
              name="device_owner"
              className="form-control"
              title="Select..."
              value={owner}
              onChange={setOwner}
              options={owners.map((o) => ({ value: String(o.id), label: o.name }))}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>Office</span>
          </label>
          <div className="col-sm-9">
            <select name="device_office" className="form-control" value={office} onChange={(e) => setOffice(e.target.value)}>
              <option value="">Select...</option>
              {offices.map((o) => (
                <option key={o.id} value={String(o.id)}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>Classified info</span>
          </label>
          <div className="col-sm-9">
            <textarea
              style={{ maxWidth: '100%' }}
              className="form-control"
              name="device_classified_info"
              value={classified}
              onChange={(e) => setClassified(e.target.value)}
            ></textarea>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>Agent install settings</span>
          </label>
          <div className="col-sm-9">
            <label className="checkbox-inline" style={{ paddingLeft: 0, marginRight: 50 }}>
              <ICheck name="agent_auto_update" value="1" className="iCheckSettings" checked={autoUpdate} onChange={setAutoUpdate} /> Auto update
            </label>
            <label className="checkbox-inline" style={{ paddingLeft: 0 }}>
              <ICheck name="agent_beta_version" value="1" className="iCheckSettings" checked={beta} onChange={setBeta} /> Beta version
            </label>
          </div>
        </div>
        <div className="form-group offset">
          <div className="col-md-offset-3 col-md-9">
            <button type="submit" className="btn btn-theme">
              Save
            </button>
          </div>
        </div>
      </form>
    </Collapse>
  )
}

/** `overview/multiple_messages` -> `overview/sendMessage`. */
function MessageTab({ ids }: { ids: number[] }) {
  const [message, setMessage] = useState('')
  const [kind, setKind] = useState('system_message')

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (message.trim() === '') return
    if (!confirmed()) return
    void sendMultiMessage(ids, message, kind)
      .then(() => {
        toast('Message sent!', 'Success', 'success')
        setMessage('')
      })
      .catch(apiToast)
  }

  return (
    <Collapse title="Send Messages">
      <form className="form-horizontal deviceMessage" method="post" onSubmit={submit}>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>Message</span>
          </label>
          <div className="col-sm-9">
            <textarea
              style={{ maxWidth: '100%' }}
              className="form-control"
              name="message"
              required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            ></textarea>
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>System message</span>
          </label>
          <div className="col-sm-9">
            <ICheck
              type="radio"
              name="message_type"
              value="system_message"
              className="iCheckSettings"
              checked={kind === 'system_message'}
              onChange={() => setKind('system_message')}
            />
          </div>
        </div>
        <div className="form-group">
          <label className="col-sm-3 control-label">
            <span>System tray bubble</span>
          </label>
          <div className="col-sm-9">
            <ICheck
              type="radio"
              name="message_type"
              value="system_tray_bubble"
              className="iCheckSettings"
              checked={kind === 'system_tray_bubble'}
              onChange={() => setKind('system_tray_bubble')}
            />
          </div>
        </div>
        <div className="form-group offset">
          <div className="col-md-offset-3 col-md-9">
            <button type="submit" className="btn btn-theme">
              Save
            </button>
          </div>
        </div>
      </form>
    </Collapse>
  )
}

/**
 * `overview/multiple_actions`.
 *
 * "Restart device" lipseste si aici: in macheta veche butonul e comentat, iar
 * `DeviceMultiActions` ii leaga un `click` gol. Nu se deseneaza un buton care
 * n-a facut nimic.
 */
function ActionsTab({ ids, can }: { ids: number[]; can: { wake: boolean; check: boolean; remove: boolean; notify: boolean } }) {
  const [notify, setNotify] = useState(false)

  const relayToast = (saved: MultiSaved) => {
    if ((saved.sent ?? 0) === 0 && (saved.noRelay ?? 0) > 0) {
      toast('No ad relay device!', 'Error', 'error')
      return false
    }
    return true
  }

  const wake = () => {
    if (!confirmed('Are you sure ?')) return
    void multiWakeUp(ids)
      .then((saved) => {
        if (relayToast(saved)) toast('Wake up command has been sent.', 'Success', 'success')
      })
      .catch(apiToast)
  }

  const check = () => {
    void multiCheckAlive(ids)
      .then((saved) => {
        if (relayToast(saved)) toast('Instruction sent.', 'Success', 'success')
      })
      .catch(apiToast)
  }

  const remove = () => {
    if (!confirmed()) return
    void multiUninstall(ids)
      .then(() => toast('Uninstall command has been sent!', 'Success', 'success'))
      .catch(apiToast)
  }

  const onNotify = (on: boolean) => {
    setNotify(on)
    void multiNotifyWhenAlive(ids, on ? 1 : 0)
      .then(() => toast('Instruction sent.', 'Success', 'success'))
      .catch((err: unknown) => {
        setNotify(!on)
        apiToast(err)
      })
  }

  return (
    <Collapse title="DEVICE ACTIONS">
      <div>
        {(can.wake || can.check) && (
          <div className="form-group">
            {can.wake && (
              <button className="btn btn-white multi-lan-wake-up" type="button" onClick={wake}>
                <i className="far fa-bell"></i> On Lan Wake Up
              </button>
            )}{' '}
            {can.check && (
              <button className="btn btn-white multi-check-if-alive" type="button" onClick={check}>
                <i className="fas fa-search" aria-hidden="true"></i> Check if alive
              </button>
            )}
          </div>
        )}
        {can.remove && (
          <div className="form-group">
            <button className="btn btn-danger multi-remove-optimumdesk" type="button" onClick={remove}>
              <i className="fa fa-trash"></i> Remove Optimumdesk
            </button>
          </div>
        )}
        {can.notify && (
          <div className="form-group">
            <label className="checkbox-inline" style={{ paddingLeft: 0 }}>
              <ICheck id="multi-notify-when-alive" name="notify_when_alive" value="1" className="iCheckSettings" checked={notify} onChange={onNotify} />{' '}
              Notify me when alive
            </label>
          </div>
        )}
      </div>
    </Collapse>
  )
}

/**
 * `overview/multiple_modules` - tabelul nou, acelasi cu al fisei aparatului:
 * vezi `MultiModulesTab.tsx`.
 */
function ModulesTab({ ids }: { ids: number[] }) {
  return (
    <Collapse title="DEVICE MODULES">
      <MultiModulesTab ids={ids} />
    </Collapse>
  )
}

export function MultiActionsSidebar({ ids }: { ids: number[] }) {
  const key = ids.join(',')
  const tabs = useQuery(multiActionsTabsQuery)
  const [dismissed, setDismissed] = useState(false)
  const [active, setActive] = useState<MultiTabKey | null>(null)

  /* Portalul redeschide panoul la orice schimbare de bifa, si asa se pierde si
     inchiderea de mana - vezi `multiRowsChange` -> `openSidebar`. */
  useEffect(() => {
    setDismissed(false)
  }, [key])

  const allowed = tabs.data?.tabs ?? []
  const can = tabs.data?.actions ?? { wake: false, check: false, remove: false, notify: false }
  /* O fila care n-are ce arata nu se deseneaza: fara niciunul din cele patru
     drepturi de actiune, `Actions` era in portal o fila goala. */
  const drawn = allowed.filter((tab) => tab.key !== 'actions' || can.wake || can.check || can.remove || can.notify)
  const current = active !== null && drawn.some((t) => t.key === active) ? active : (drawn[0]?.key ?? null)
  const open = ids.length > 0 && !dismissed && drawn.length > 0

  return (
    <div className={`multiactions-sidebar${open ? ' page-quick-sidebar-open' : ''}`}>
      <div className="page-quick-sidebar-wrapper quick-sidebar-navigation" style={{ width: '50%' }}>
        <div className="page-quick-sidebar-actions">
          <a className="close-sidebar" role="button" tabIndex={0} onClick={() => setDismissed(true)} onKeyDown={(e) => e.key === 'Enter' && setDismissed(true)}>
            <i className="fas fa-indent"></i>
          </a>
        </div>
        <div className="page-quick-sidebar avatar-slide">
          {open && (
            <>
              <div className="multiple-actions-nav">
                {drawn.map((tab) => (
                  <a
                    key={tab.key}
                    className={`btn btn-multi-tabs${current === tab.key ? ' active' : ''}`}
                    href="#"
                    title={tab.label}
                    onClick={(e) => {
                      e.preventDefault()
                      setActive(tab.key)
                    }}
                  >
                    <i className={tab.icon}></i> {tab.label}
                  </a>
                ))}
              </div>
              <div id="multiple-actions-content">
                {current === 'settings' && <SettingsTab key={key} ids={ids} />}
                {current === 'message' && <MessageTab key={key} ids={ids} />}
                {current === 'actions' && <ActionsTab key={key} ids={ids} can={can} />}
                {current === 'modules' && <ModulesTab key={key} ids={ids} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
